import { httpClient, unwrap, unwrapPaginated } from '@/services/httpClient';
import type {
  CustomFieldDefinition,
  CustomFieldValue,
  Project,
  ProjectComment,
  ProjectDashboardSummary,
  ProjectDependencies,
  ProjectLabel,
  ProjectMember,
  ProjectMemberRole,
  ProjectMilestone,
  ProjectPriorityOption,
  ProjectReports,
  ProjectRisk,
  ProjectStatusOption,
  ProjectTag,
  ProjectTemplate,
  ProjectVisibility,
} from '@/features/projects/types';

export interface ProjectPayload {
  workspace_id: number;
  client_id?: number | null;
  template_id?: number | null;
  status_id?: number | null;
  priority_id?: number | null;
  name: string;
  code?: string;
  description?: string;
  start_date?: string | null;
  due_date?: string | null;
  budget?: number | null;
  budget_currency?: string;
  is_archived?: boolean;
  visibility?: ProjectVisibility;
  color?: string;
  icon?: string;
}

export interface ProjectFilters {
  workspaceId?: number;
  clientId?: number;
  statusId?: number;
  priorityId?: number;
  ownerId?: number;
  isArchived?: boolean;
}

function filterParams(filters?: ProjectFilters) {
  return {
    workspace_id: filters?.workspaceId || undefined,
    client_id: filters?.clientId || undefined,
    status_id: filters?.statusId || undefined,
    priority_id: filters?.priorityId || undefined,
    owner_id: filters?.ownerId || undefined,
    is_archived: filters?.isArchived === undefined ? undefined : filters.isArchived ? 1 : 0,
  };
}

export const projectsApi = {
  list: (page: number, perPage: number, search: string, sort: string, order: 'asc' | 'desc', filters?: ProjectFilters) =>
    unwrapPaginated<Project>(
      httpClient.get('/projects', { params: { page, per_page: perPage, search: search || undefined, sort, order, ...filterParams(filters) } }),
    ),

  get: (id: number) => unwrap<Project>(httpClient.get(`/projects/${id}`)),

  create: (payload: ProjectPayload) => unwrap<Project>(httpClient.post('/projects', payload)),

  update: (id: number, payload: Partial<ProjectPayload>) => unwrap<Project>(httpClient.put(`/projects/${id}`, payload)),

  remove: (id: number) => unwrap<null>(httpClient.delete(`/projects/${id}`)),

  duplicate: (id: number, name?: string) => unwrap<Project>(httpClient.post(`/projects/${id}/duplicate`, { name })),

  archive: (id: number) => unwrap<Project>(httpClient.post(`/projects/${id}/archive`)),

  restore: (id: number) => unwrap<Project>(httpClient.post(`/projects/${id}/restore`)),

  statuses: (workspaceId?: number) => unwrap<ProjectStatusOption[]>(httpClient.get('/project-statuses', { params: { workspace_id: workspaceId } })),

  priorities: () => unwrap<ProjectPriorityOption[]>(httpClient.get('/project-priorities')),

  createStatus: (payload: { name: string; color?: string; category?: string; sort_order?: number; is_default?: boolean }) =>
    unwrap<ProjectStatusOption>(httpClient.post('/project-statuses', payload)),
  updateStatus: (id: number, payload: Partial<{ name: string; color: string; category: string; sort_order: number; is_default: boolean }>) =>
    unwrap<ProjectStatusOption>(httpClient.put(`/project-statuses/${id}`, payload)),
  deleteStatus: (id: number) => unwrap<null>(httpClient.delete(`/project-statuses/${id}`)),

  createPriority: (payload: { name: string; color?: string; level?: number }) =>
    unwrap<ProjectPriorityOption>(httpClient.post('/project-priorities', payload)),
  updatePriority: (id: number, payload: Partial<{ name: string; color: string; level: number }>) =>
    unwrap<ProjectPriorityOption>(httpClient.put(`/project-priorities/${id}`, payload)),
  deletePriority: (id: number) => unwrap<null>(httpClient.delete(`/project-priorities/${id}`)),

  // Members
  addMember: (id: number, userId: number, role: ProjectMemberRole = 'member', hourlyRate?: number) =>
    unwrap<ProjectMember[]>(httpClient.post(`/projects/${id}/members`, { user_id: userId, role_in_project: role, hourly_rate: hourlyRate })),
  removeMember: (id: number, userId: number) => unwrap<ProjectMember[]>(httpClient.delete(`/projects/${id}/members/${userId}`)),

  // Milestones
  milestones: (id: number) => unwrap<ProjectMilestone[]>(httpClient.get(`/projects/${id}/milestones`)),
  createMilestone: (id: number, payload: { name: string; description?: string; due_date?: string; sort_order?: number }) =>
    unwrap<ProjectMilestone>(httpClient.post(`/projects/${id}/milestones`, payload)),
  updateMilestone: (id: number, milestoneId: number, payload: Partial<{ name: string; description: string; due_date: string; completed_at: string | null; sort_order: number }>) =>
    unwrap<ProjectMilestone>(httpClient.put(`/projects/${id}/milestones/${milestoneId}`, payload)),
  deleteMilestone: (id: number, milestoneId: number) => unwrap<null>(httpClient.delete(`/projects/${id}/milestones/${milestoneId}`)),

  // Color Tags
  tags: (id: number) => unwrap<ProjectTag[]>(httpClient.get(`/projects/${id}/tags`)),
  addTag: (id: number, tag: string, color?: string) => unwrap<ProjectTag>(httpClient.post(`/projects/${id}/tags`, { tag, color })),
  removeTag: (id: number, tagId: number) => unwrap<null>(httpClient.delete(`/projects/${id}/tags/${tagId}`)),

  // Labels
  labelCatalog: (workspaceId?: number) => unwrap<ProjectLabel[]>(httpClient.get('/project-labels', { params: { workspace_id: workspaceId } })),
  createLabel: (name: string, color?: string, workspaceId?: number) =>
    unwrap<ProjectLabel>(httpClient.post('/project-labels', { name, color, workspace_id: workspaceId })),
  deleteLabelFromCatalog: (labelId: number) => unwrap<null>(httpClient.delete(`/project-labels/${labelId}`)),
  projectLabels: (id: number) => unwrap<ProjectLabel[]>(httpClient.get(`/projects/${id}/labels`)),
  attachLabel: (id: number, labelId: number) => unwrap<ProjectLabel[]>(httpClient.post(`/projects/${id}/labels`, { label_id: labelId })),
  detachLabel: (id: number, labelId: number) => unwrap<ProjectLabel[]>(httpClient.delete(`/projects/${id}/labels/${labelId}`)),

  // Activity / Dashboard / Reports
  activity: (id: number, page = 1) => unwrapPaginated(httpClient.get(`/projects/${id}/activity`, { params: { page, per_page: 25 } })),
  dashboard: (id: number) => unwrap<ProjectDashboardSummary>(httpClient.get(`/projects/${id}/dashboard`)),
  reports: (id: number) => unwrap<ProjectReports>(httpClient.get(`/projects/${id}/reports`)),

  // Custom Fields (values)
  customFields: (id: number) => unwrap<CustomFieldValue[]>(httpClient.get(`/projects/${id}/custom-fields`)),
  updateCustomFields: (id: number, values: Record<number, string | null>) =>
    unwrap<CustomFieldValue[]>(httpClient.put(`/projects/${id}/custom-fields`, { values })),

  // Discussion
  comments: (id: number) => unwrap<ProjectComment[]>(httpClient.get(`/projects/${id}/comments`)),
  createComment: (id: number, content: string, parentCommentId?: number) =>
    unwrap<ProjectComment>(httpClient.post(`/projects/${id}/comments`, { content, parent_comment_id: parentCommentId })),
  updateComment: (id: number, commentId: number, content: string) =>
    unwrap<ProjectComment>(httpClient.put(`/projects/${id}/comments/${commentId}`, { content })),
  deleteComment: (id: number, commentId: number) => unwrap<null>(httpClient.delete(`/projects/${id}/comments/${commentId}`)),

  // Risks
  risks: (id: number, filters?: { risk_status?: string; severity?: string }) =>
    unwrap<ProjectRisk[]>(httpClient.get(`/projects/${id}/risks`, { params: filters })),
  createRisk: (id: number, payload: Partial<ProjectRisk> & { title: string }) => unwrap<ProjectRisk>(httpClient.post(`/projects/${id}/risks`, payload)),
  updateRisk: (id: number, riskId: number, payload: Partial<ProjectRisk>) => unwrap<ProjectRisk>(httpClient.put(`/projects/${id}/risks/${riskId}`, payload)),
  deleteRisk: (id: number, riskId: number) => unwrap<null>(httpClient.delete(`/projects/${id}/risks/${riskId}`)),

  // Dependencies
  dependencies: (id: number) => unwrap<ProjectDependencies>(httpClient.get(`/projects/${id}/dependencies`)),
  addDependency: (id: number, dependsOnProjectId: number, type: 'blocks' | 'related_to' = 'blocks') =>
    unwrap<ProjectDependencies>(httpClient.post(`/projects/${id}/dependencies`, { depends_on_project_id: dependsOnProjectId, dependency_type: type })),
  removeDependency: (id: number, dependencyId: number) => unwrap<null>(httpClient.delete(`/projects/${id}/dependencies/${dependencyId}`)),
};

export interface ClientOption {
  id: number;
  name: string;
}

/** Minimal client picker — the full Clients module (contacts, contracts, portal) isn't built yet; this only covers the `client_id` dropdown on a project. */
export const clientsApi = {
  list: () =>
    unwrapPaginated<ClientOption>(httpClient.get('/clients', { params: { page: 1, per_page: 100 } }))
      .then((r) => r.data)
      .catch(() => [] as ClientOption[]),
};

export const projectTemplatesApi = {
  list: () => unwrap<ProjectTemplate[]>(httpClient.get('/project-templates')),
  create: (payload: { name: string; description?: string; category?: string; from_project_id?: number }) =>
    unwrap<ProjectTemplate>(httpClient.post('/project-templates', payload)),
  remove: (id: number) => unwrap<null>(httpClient.delete(`/project-templates/${id}`)),
};

export const customFieldsApi = {
  definitions: (entityType = 'project') => unwrap<CustomFieldDefinition[]>(httpClient.get('/custom-fields', { params: { entity_type: entityType } })),
  create: (payload: { field_key: string; label: string; field_type?: string; options?: string[]; is_required?: boolean; entity_type?: string }) =>
    unwrap<CustomFieldDefinition>(httpClient.post('/custom-fields', payload)),
  update: (id: number, payload: Partial<{ label: string; field_type: string; options: string[]; is_required: boolean; sort_order: number }>) =>
    unwrap<CustomFieldDefinition>(httpClient.put(`/custom-fields/${id}`, payload)),
  remove: (id: number) => unwrap<null>(httpClient.delete(`/custom-fields/${id}`)),
};
